import React from "react";
import {
  Alert,
  Box,
  Chip,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import { Link, useSearchParams } from "react-router-dom";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import TableFilter from "components/Shared/AppTable/TableFilter";
import AppTable from "components/Shared/AppTable";
import BaseSelect from "components/Shared/Base/BaseSelect";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";

import { useLazyLateShipmentListQuery } from "store/services/reportApi";
import {
  useLazyGetDDLBranchListQuery,
  useLazyGetDDLDestinationBranchListQuery,
  useLazyGetBranchByRegionDDLQuery,
} from "store/services/branchApi";
import {
  useLazyGetDdlRegionListQuery,
  useLazyGetDDLRestrictedRegionListQuery,
} from "store/services/LocationsApi/regionApi";

import { useAuth, useTablePagination } from "hooks";
import { useCustomParams } from "hooks/useCustomParams";
import ExportLateShipmentExcel from "./ExportLateShipmentExcel";

const getStatusStyle = (status) => {
  const styles = {
    Pending: { backgroundColor: "#F8C471", textColor: "#D35400" },
    Collected: { backgroundColor: "#D4E6F1", textColor: "#2E86C1" },
    Loaded: { backgroundColor: "#EBF5FB", textColor: "#3498DB" },
    Unloaded: { backgroundColor: "#FEF9E7", textColor: "#B7950B" },
    CrossDocked: { backgroundColor: "#E8DAEF", textColor: "#8E44AD" },
    SendOut: { backgroundColor: "#FADBD8", textColor: "#E74C3C" },
    Delivered: { backgroundColor: "#DFF7DF", textColor: "green" },
    DeliveryFailed: { backgroundColor: "#FADBD8", textColor: "#C0392B" },
  };
  return styles[status] || { backgroundColor: "#F8F9F9", textColor: "#7F8C8D" };
};

const ShipmentReportListScreen = () => {
  const { isVendor, isCustomer, viewEntity } = useAuth();

  const { currentPage, perPage, handlePageChange, handlePerRowsChange } = useTablePagination();
  const [searchParams] = useSearchParams();
  const keyword = searchParams.get("search") || "";

  const { params, appendParams, resetParams } = useCustomParams();

  const status = params.status || "";
  const deliveryTypes = params.deliveryTypes || "";
  const lateShipment = params.lateShipment || "5";
  const destinationRegion = params.destinationRegion || "";
  const sourceRegion = params.sourceRegion || "";

  const destinationBranches = React.useMemo(
    () => params.destinationBranches?.split(",").filter(Boolean) || [],
    [params.destinationBranches]
  );
  const sourceBranches = React.useMemo(
    () => params.sourceBranches?.split(",").filter(Boolean) || [],
    [params.sourceBranches]
  );

  // Branch DDLs
  const [triggerGetSourceBranches, { data: sourceBranchesAllData, isLoading: loadingSourceBranches }] =
    useLazyGetDDLBranchListQuery();
  const [triggerGetDestinationBranches, { data: destinationBranchesAllData, isLoading: loadingDestinationBranches }] =
    useLazyGetDDLDestinationBranchListQuery();

  // Region DDLs
  const [triggerGetRestrictedRegionList, { data: restrictedRegionsData }] =
    useLazyGetDDLRestrictedRegionListQuery();
  const [triggerGetDdlRegionList, { data: regionsData }] =
    useLazyGetDdlRegionListQuery();

  const [triggerGetDestinationBranchesByRegion, { data: destinationBranchesData }] = useLazyGetBranchByRegionDDLQuery();
  const [triggerGetSourceBranchesByRegion, { data: sourceBranchesByRegionData }] = useLazyGetBranchByRegionDDLQuery();

  const breadcrumbs = [
    { title: "Reports", path: "/app/crm/reports/list" },
    { title: "Late Shipment Reports" },
  ];

  // Source Province — restricted
  const mappedRestrictedRegionList = React.useMemo(() => {
    const dataArray = Array.isArray(restrictedRegionsData)
      ? restrictedRegionsData
      : Array.isArray(restrictedRegionsData?.data)
        ? restrictedRegionsData.data
        : [];
    return dataArray
      .filter((item) => item?._id && item?.name)
      .map((item) => ({ label: item.name, value: item._id }));
  }, [restrictedRegionsData]);

  // Destination Province — all regions
  const mappedRegionList = React.useMemo(() => {
    const dataArray = Array.isArray(regionsData)
      ? regionsData
      : Array.isArray(regionsData?.data)
        ? regionsData.data
        : [];
    return dataArray
      .filter((item) => item?._id && item?.name)
      .map((item) => ({ label: item.name, value: item._id }));
  }, [regionsData]);

  const mappedDestinationRegionBranches = React.useMemo(
    () =>
      Array.isArray(destinationBranchesData?.data)
        ? destinationBranchesData.data
          .filter((item) => item?._id && item?.name && item?.code)
          .map((item) => ({ label: `${item.name} - ${item.code}`, value: item._id }))
        : [],
    [destinationBranchesData]
  );

  const selectedDestinationRegion = React.useMemo(
    () => mappedRegionList?.find((region) => region.value === destinationRegion),
    [mappedRegionList, destinationRegion]
  );

  const destinationBranchIdsFromRegion = React.useMemo(
    () => mappedDestinationRegionBranches.map((branch) => branch.value),
    [mappedDestinationRegionBranches]
  );

  const mappedSourceRegionBranches = React.useMemo(
    () =>
      Array.isArray(sourceBranchesByRegionData?.data)
        ? sourceBranchesByRegionData.data
          .filter((item) => item?._id && item?.name && item?.code)
          .map((item) => ({ label: `${item.name} - ${item.code}`, value: item._id }))
        : [],
    [sourceBranchesByRegionData]
  );

  const selectedSourceRegion = React.useMemo(
    () => mappedRestrictedRegionList?.find((region) => region.value === sourceRegion),
    [mappedRestrictedRegionList, sourceRegion]
  );

  const sourceBranchIdsFromRegion = React.useMemo(
    () => mappedSourceRegionBranches.map((branch) => branch.value),
    [mappedSourceRegionBranches]
  );

  const columns = [
    {
      name: "Shipment ID",
      selector: (row) => row.shipmentId,
      cell: (row) => row.shipmentId,
    },
    {
      name: "Sender",
      selector: (row) => row?.customerUser?.name || row?.vendor?.shopName || "N/A",
      cell: (row) => (
        <Box sx={{ maxWidth: "600px", overflow: "hidden", textOverflow: "ellipsis" }}>
          {row?.customerUser?.name
            ? `CUS: ${row.customerUser.name}`
            : row?.vendor?.shopName
              ? `VEN: ${row.vendor.shopName}`
              : "N/A"}
        </Box>
      ),
      omit: isVendor || isCustomer,
    },
    {
      name: "Receiver",
      selector: (row) => row?.receiver?.customerUser?.name,
      cell: (row) => (
        <div>
          <span className="text-sm">{row?.receiver?.customerUser?.name || "N/A"}</span>
          <br />
          <span className="text-sm text-gray-500 dark:text-gray-300">{row?.receiver?.phone || ""}</span>
        </div>
      ),
    },
    {
      name: "From",
      selector: (row) => row?.sourceBranch?.name,
      cell: (row) => row?.sourceBranch?.name || "N/A",
    },
    {
      name: "To",
      selector: (row) => row?.destinationBranch?.name,
      cell: (row) => row?.destinationBranch?.name || "N/A",
    },
    {
      name: "Duration",
      selector: (row) => row.totalDays,
      cell: (row) => `${Math.floor(row.totalDays)} days` || "N/A",
    },
    {
      name: "Status",
      selector: (row) => row.status,
      cell: (row) => (
        <Box display="flex" justifyContent="center" alignItems="center">
          <Chip
            label={row?.status?.toUpperCase() || "N/A"}
            variant="outlined"
            sx={{
              backgroundColor: getStatusStyle(row?.status).backgroundColor,
              color: getStatusStyle(row?.status).textColor,
              fontWeight: "bold",
              height: "17px",
              borderRadius: "12px",
              width: "auto",
              padding: "0",
              border: "none",
              "& .MuiChip-label": {
                fontSize: "0.6rem",
                padding: "3px 5px",
                lineHeight: "14px",
              },
            }}
          />
        </Box>
      ),
      center: true,
    },
    {
      name: "Actions",
      cell: (row) => (
        <Box display="flex" justifyContent="center" alignItems="center" gap={1}>
          <Link to={`/app/crm/shipment/${row?._id}/detail`}>
            <IconButton size="small" aria-label="View Shipment" color="primary">
              <InfoOutlined />
            </IconButton>
          </Link>
        </Box>
      ),
      ignoreRowClick: true,
      right: true,
    },
  ];

  const [fetchShipment, { data: shipmentRes, isLoading, isFetching, error }] = useLazyLateShipmentListQuery();

  const fetchRegions = () => {
    if (viewEntity?.entity?.account?._id && viewEntity?.entity?._id) {
      triggerGetDdlRegionList({
        account: viewEntity.entity.account._id,
        entity: viewEntity.entity._id,
      }).unwrap().catch((error) => console.error("Failed to fetch regions:", error));

      triggerGetRestrictedRegionList({
        account: viewEntity.entity.account._id,
        entity: viewEntity.entity._id,
      }).unwrap().catch((error) => console.error("Failed to fetch restricted regions:", error));
    }
  };

  const handleDestinationRegionChange = (_, selectedRegion) => {
    const regionId = selectedRegion?.value || "";

    appendParams({
      destinationRegion: regionId,
      destinationBranches: "",
    });

    if (regionId && viewEntity?.entity?.account?._id && viewEntity?.entity?._id) {
      triggerGetDestinationBranchesByRegion({
        regionId,
        account: viewEntity.entity.account._id,
        entity: viewEntity.entity._id,
      }).unwrap().catch((error) => console.error("Failed to fetch destination region branches:", error));
    }
  };

  const handleSourceRegionChange = (_, selectedRegion) => {
    const regionId = selectedRegion?.value || "";

    appendParams({
      sourceRegion: regionId,
      sourceBranches: "",
    });

    if (regionId && viewEntity?.entity?.account?._id && viewEntity?.entity?._id) {
      triggerGetSourceBranchesByRegion({
        regionId,
        account: viewEntity.entity.account._id,
        entity: viewEntity.entity._id,
      }).unwrap().catch((error) => console.error("Failed to fetch source region branches:", error));
    }
  };

  // Fetch branch DDLs on mount
  React.useEffect(() => {
    if (viewEntity?.entity?.account?._id && viewEntity?.entity?._id) {
      triggerGetSourceBranches({
        account: viewEntity.entity.account._id,
        entity: viewEntity.entity._id,
      });
      triggerGetDestinationBranches({
        account: viewEntity.entity.account._id,
        entity: viewEntity.entity._id,
      });
    }
  }, [viewEntity, triggerGetSourceBranches, triggerGetDestinationBranches]);

  const finalDestinationBranchIds = React.useMemo(
    () => (destinationRegion ? destinationBranchIdsFromRegion : destinationBranches),
    [destinationRegion, destinationBranchIdsFromRegion, destinationBranches]
  );

  const finalSourceBranchIds = React.useMemo(
    () => (sourceRegion ? sourceBranchIdsFromRegion : sourceBranches),
    [sourceRegion, sourceBranchIdsFromRegion, sourceBranches]
  );

  React.useEffect(() => {
    fetchShipment({
      page: currentPage,
      limit: perPage,
      ...(keyword && { keyword }),
      ...(deliveryTypes && { types: deliveryTypes }),
      ...(finalDestinationBranchIds.length > 0 && {
        destinationBranch: finalDestinationBranchIds,
      }),
      ...(finalSourceBranchIds.length > 0 && {
        sourceBranch: finalSourceBranchIds,
      }),
      ...(status && { status }),
      lateShipment,
      ...(!status && { excludeDelivered: true }),
    });
  }, [
    currentPage,
    deliveryTypes,
    fetchShipment,
    keyword,
    perPage,
    lateShipment,
    status,
    finalDestinationBranchIds,
    finalSourceBranchIds,
  ]);

  React.useEffect(() => {
    if (viewEntity?.entity?.account?._id && viewEntity?.entity?._id) {
      triggerGetDdlRegionList({
        account: viewEntity.entity.account._id,
        entity: viewEntity.entity._id,
      });

      triggerGetRestrictedRegionList({
        account: viewEntity.entity.account._id,
        entity: viewEntity.entity._id,
      });

      if (sourceRegion) {
        triggerGetSourceBranchesByRegion({
          regionId: sourceRegion,
          account: viewEntity.entity.account._id,
          entity: viewEntity.entity._id,
        });
      }

      if (destinationRegion) {
        triggerGetDestinationBranchesByRegion({
          regionId: destinationRegion,
          account: viewEntity.entity.account._id,
          entity: viewEntity.entity._id,
        });
      }
    }
  }, [
    viewEntity?.entity?.account?._id,
    viewEntity?.entity?._id,
    sourceRegion,
    destinationRegion,
    triggerGetDdlRegionList,
    triggerGetRestrictedRegionList,
    triggerGetSourceBranchesByRegion,
    triggerGetDestinationBranchesByRegion,
  ]);

  const handleChange = (event, newShipment) => {
    if (newShipment) {
      appendParams({ lateShipment: newShipment, page: 1 });
    }
  };

  // Source branch options — restricted
  const sourceBranchOptions = React.useMemo(() => {
    const dataArray = Array.isArray(sourceBranchesAllData)
      ? sourceBranchesAllData
      : Array.isArray(sourceBranchesAllData?.data)
        ? sourceBranchesAllData.data
        : [];
    return dataArray.filter((b) => b?._id && b?.name);
  }, [sourceBranchesAllData]);

  // Destination branch options — all branches
  const destinationBranchOptions = React.useMemo(() => {
    const dataArray = Array.isArray(destinationBranchesAllData)
      ? destinationBranchesAllData
      : Array.isArray(destinationBranchesAllData?.data)
        ? destinationBranchesAllData.data
        : [];
    return dataArray.filter((b) => b?._id && b?.name);
  }, [destinationBranchesAllData]);

  const selectedDestinationBranches = React.useMemo(() => {
    return destinationBranchOptions.filter((branch) => destinationBranches.includes(branch._id));
  }, [destinationBranchOptions, destinationBranches]);

  const selectedSourceBranches = React.useMemo(() => {
    return sourceBranchOptions.filter((branch) => sourceBranches.includes(branch._id));
  }, [sourceBranchOptions, sourceBranches]);

  const BoundExportLateShipmentExcel = React.useCallback(
    () => (
      <ExportLateShipmentExcel
        lateShipment={lateShipment}
        destinationBranch={finalDestinationBranchIds}
        sourceBranch={finalSourceBranchIds}
        status={status}
        deliveryTypes={deliveryTypes}
        keyword={keyword}
      />
    ),
    [lateShipment, finalDestinationBranchIds, finalSourceBranchIds, status, deliveryTypes, keyword]
  );

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs}>
      <BreadcrumbLayout.Error error={error} />
      <BreadcrumbLayout.Paper>
        <TableFilter
          searchLabel="Shipment ID"
          disabled={isLoading || isFetching || loadingSourceBranches || loadingDestinationBranches}
          initialFilters={3}
          exportComponent={BoundExportLateShipmentExcel}
          onClear={() => resetParams()}
        >
          <ToggleButtonGroup
            color="primary"
            value={lateShipment}
            exclusive
            size="small"
            onChange={handleChange}
            aria-label="Late Shipment Filter"
            wrapperClassName="w-auto"
            sx={{ flexWrap: "nowrap" }}
          >
            <ToggleButton value="3">3 Days</ToggleButton>
            <ToggleButton value="5">5 Days</ToggleButton>
            <ToggleButton value="7">1 Week</ToggleButton>
            <ToggleButton value="15">15 days</ToggleButton>
            <ToggleButton value="30">1 Month</ToggleButton>
          </ToggleButtonGroup>

          {/* Destination Branches — all branches */}
          <BaseAutocomplete
            fullWidth
            multiple
            label="Destination Branches"
            value={selectedDestinationBranches}
            onChange={(_, data) => {
              appendParams({
                destinationBranches: data.map((branch) => branch._id).join(","),
                destinationRegion: "",
              });
            }}
            getOptionLabel={(option) => option?.name || "Unknown Branch"}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            options={destinationBranchOptions}
            loading={loadingDestinationBranches}
            loadingText="Loading Branches..."
            disabled={isLoading || isFetching || loadingDestinationBranches || !!destinationRegion}
          />

          {/* Destination Province — all regions */}
          <BaseAutocomplete
            fullWidth
            label="Destination Province"
            value={selectedDestinationRegion || null}
            options={mappedRegionList}
            onChange={handleDestinationRegionChange}
            onFocus={fetchRegions}
            getOptionLabel={(opt) => opt?.label || ""}
            isOptionEqualToValue={(opt, value) => opt?.value === value?.value}
            disabled={isLoading || isFetching}
          />

          {/* Source Branches — restricted */}
          <BaseAutocomplete
            fullWidth
            multiple
            label="Source Branches"
            value={selectedSourceBranches}
            onChange={(_, data) => {
              appendParams({
                sourceBranches: data.map((branch) => branch._id).join(","),
                sourceRegion: "",
              });
            }}
            getOptionLabel={(option) => option?.name || "Unknown Branch"}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            options={sourceBranchOptions}
            loading={loadingSourceBranches}
            loadingText="Loading Branches..."
            disabled={isLoading || isFetching || loadingSourceBranches || !!sourceRegion}
          />

          {/* Source Province — restricted */}
          <BaseAutocomplete
            fullWidth
            label="Source Province"
            value={selectedSourceRegion || null}
            options={mappedRestrictedRegionList}
            onChange={handleSourceRegionChange}
            onFocus={fetchRegions}
            getOptionLabel={(opt) => opt?.label || ""}
            isOptionEqualToValue={(opt, value) => opt?.value === value?.value}
            disabled={isLoading || isFetching}
          />

          <BaseSelect
            fullWidth
            label="By Status"
            value={status}
            onChange={(e) => appendParams({ status: e.target.value })}
            options={[
              { label: "All Statuses", value: "" },
              { label: "Pending", value: "Pending" },
              { label: "Collected", value: "Collected" },
              { label: "Loaded", value: "Loaded" },
              { label: "Unloaded", value: "Unloaded" },
              { label: "CrossDocked", value: "CrossDocked" },
              { label: "SendOut", value: "SendOut" },
              { label: "Warehousing", value: "Warehousing" },
              { label: "DeliveryFailed", value: "DeliveryFailed" },
              { label: "Return", value: "Return" },
              { label: "Cancelled", value: "Cancelled" },
            ]}
            getOptionLabel={(opt) => opt.label}
            getOptionValue={(opt) => opt.value}
          />

          <BaseSelect
            fullWidth
            label="Delivery Types"
            value={deliveryTypes}
            onChange={(e) => appendParams({ deliveryTypes: e.target.value })}
            options={[
              { label: "All Types", value: "" },
              { label: "Express", value: "Express" },
              { label: "Regular", value: "Regular" },
            ]}
            getOptionLabel={(opt) => opt.label}
            getOptionValue={(opt) => opt.value}
          />
        </TableFilter>
        <Alert severity="info" sx={{ mx: 2, mb: 2 }}>
          Duration is calculated from the shipment's creation date. Delivered shipments are excluded unless a status filter is applied. Results are limited to branches assigned to you.
        </Alert>
        <AppTable
          columns={columns}
          data={shipmentRes?.data ?? []}
          progressPending={isLoading || isFetching || loadingSourceBranches || loadingDestinationBranches}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={shipmentRes?.totalItems ?? 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default ShipmentReportListScreen;